import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { useRecordPurchasePayment } from '@/hooks/usePropertyPurchasePayments';
import { toast } from 'sonner';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFetchAccounts } from '@accountHooks';
import { useFetchCategories } from '@metadataHooks';
import { useFetchSubCategories } from '@metadataHooks';
import { useCreateTransaction } from '@/hooks/useTransactions';
import { ConversionPreview } from '@/components/conversion-preview/ConversionPreview';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/lib/currency.utils';
import { convertTransactionAmounts } from '@/lib/currencyConversion/currencyConversion';
import { useState } from 'react';
import { fetchRatesWithRetry } from '@/integrations/api/currency';

const schema = z.object({
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  payment_date: z.string().min(1, 'Payment date is required'),
  description: z.string().nullable().optional(),
  receipt_number: z.string().nullable().optional(),
  account_id: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface PurchasePaymentFormProps {
  fixedAssetId: string;
  assetCurrency: string;
  onSuccess: () => void;
}

export function PurchasePaymentForm({ fixedAssetId, assetCurrency, onSuccess }: PurchasePaymentFormProps) {
  const [paymentCurrency, setPaymentCurrency] = useState(assetCurrency);

  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: 0,
      payment_date: new Date().toISOString().split('T')[0],
      description: '',
      receipt_number: '',
      account_id: '',
    }
  });

  const amount = watch('amount');
  const accountId = watch('account_id');

  const { data: accounts = [], isLoading: accountsLoading } = useFetchAccounts();
  const selectedAccount = accounts.find((a) => a.id === accountId);

  const { data: categories = [] } = useFetchCategories();
  const prepaymentCategory = categories.find((c) => c.name.toLowerCase() === 'prepayment');
  const { data: subCategories = [] } = useFetchSubCategories(prepaymentCategory?.id);
  const depositSubCat = subCategories.find((sc) => sc.name.toLowerCase() === 'deposit');

  const recordPayment = useRecordPurchasePayment();
  const createTransaction = useCreateTransaction();

  const onSubmit = async (data: FormData) => {
    try {
      let transactionId = null;
      let finalAmountToAsset = data.amount;

      // If user pays in a different currency, we must convert it to the asset's currency
      if (paymentCurrency !== assetCurrency) {
        const { convertedAmount } = await convertTransactionAmounts(
          {
            amount: data.amount,
            fee: 0,
            fromCurrency: paymentCurrency,
            toCurrency: assetCurrency,
          },
          fetchRatesWithRetry
        );
        finalAmountToAsset = convertedAmount;
      }

      if (data.account_id) {
        if (!prepaymentCategory) {
          throw new Error('Prepayment category not found. Please create it first.');
        }

        const tx = await createTransaction.mutateAsync({
          account_id: data.account_id,
          type: 'expense',
          amount: data.amount,
          transaction_date: data.payment_date,
          category_id: prepaymentCategory.id,
          sub_category_id: depositSubCat?.id || null,
          currency: paymentCurrency,
          description: data.description || 'Property Purchase Payment',
          receipt_number: data.receipt_number || null,
          fee: 0,
        });
        transactionId = tx.id;
      }

      await recordPayment.mutateAsync({
        fixed_asset_id: fixedAssetId,
        amount: finalAmountToAsset,
        payment_date: data.payment_date,
        description: data.description || null,
        receipt_number: data.receipt_number || null,
        transaction_id: transactionId,
      });
      toast.success('Purchase payment recorded successfully');
      onSuccess();
    } catch (error: any) {
      toast.error(`Failed to record payment: ${error.message}`);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="amount">Amount Paid</Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            {...register('amount', { valueAsNumber: true })}
          />
          {errors.amount && <p className="text-sm text-destructive">{errors.amount.message}</p>}
        </div>
        <div className="space-y-2">
          <Label>Currency</Label>
          <CurrencyToggle
            value={paymentCurrency}
            onChange={setPaymentCurrency}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Debit Account (Optional)</Label>
        <Select
          onValueChange={(val) => setValue('account_id', val === 'none' ? '' : val)}
          value={accountId || 'none'}
          disabled={accountsLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select an account to debit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Do not debit an account</SelectItem>
            {accounts.map((acc) => (
              <SelectItem key={acc.id} value={acc.id}>
                {acc.nickname || acc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {accountsLoading && accountId ? (
          <Skeleton className="h-4 w-32" />
        ) : selectedAccount?.currency ? (
          <p className="text-sm text-muted-foreground">
            {formatCurrency(selectedAccount.balance, selectedAccount.currency)}
          </p>
        ) : null}
        {selectedAccount && amount > 0 && selectedAccount.currency !== paymentCurrency && (
          <div className="mt-2">
            <ConversionPreview
              amount={amount}
              fromCurrency={paymentCurrency}
              toCurrency={selectedAccount.currency}
            />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="payment_date">Payment Date</Label>
        <Input
          id="payment_date"
          type="date"
          {...register('payment_date')}
        />
        {errors.payment_date && <p className="text-sm text-destructive">{errors.payment_date.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description (Optional)</Label>
        <Input
          id="description"
          {...register('description')}
          placeholder="e.g. June Installment"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="receipt_number">Receipt Number (Optional)</Label>
        <Input
          id="receipt_number"
          {...register('receipt_number')}
        />
      </div>

      <DialogFooter>
        <Button type="submit" disabled={isSubmitting || recordPayment.isPending || createTransaction.isPending}>
          {isSubmitting || recordPayment.isPending || createTransaction.isPending ? 'Recording...' : 'Record Payment'}
        </Button>
      </DialogFooter>
    </form>
  );
}
